import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Network, Blocks, CreditCard, Mail, ArrowRight, ChevronRight
} from 'lucide-react';
import ApiDevelopmentImg from '../../assets/Api.png'; // Assuming you have an image in assets

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';
const THEME_COLOR = '#3284c7'; // Blue theme

/* ============================== PAGE DATA ============================== */

const FEATURES = [
  { title: 'RESTful APIs', icon: Network, desc: 'Custom API architectures built for speed, security, and effortless communication between applications.' },
  { title: 'Third-party Integrations', icon: Blocks, desc: 'Connect your existing software stack with external CRM, ERP, or specialized SaaS platforms.' },
  { title: 'Payment Gateway Integration', icon: CreditCard, desc: 'Securely link your platforms with Stripe, Razorpay, PayPal, or regional banking networks.' },
  { title: 'SMS / Email / WhatsApp APIs', icon: Mail, desc: 'Automated notification pipelines to keep your customers engaged across their preferred channels.' }
];

const ApiDevelopment = () => {
  // Use location hook to get routing state
  const location = useLocation();
  const navSource = location.state?.from === 'industries' ? 'industries' : 'services';

  return (
    <div className="overflow-x-hidden pt-0">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @keyframes seFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        .se-float { animation: seFloat 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .se-float, .se-fade-up { animation: none !important; } }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#eef4fb] via-[#f5f9fd] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#3284c7]/10 blur-3xl" />
        
        <div className={`${CONTAINER} relative py-5 lg:py-6`}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-start">
            
            {/* LEFT SIDE: Text Content */}
            <div className="max-w-2xl relative z-10 se-fade-up">
              <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-6">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <Link to={`/${navSource}`} className="hover:text-primary transition-colors capitalize">{navSource}</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">API Development</span>
              </nav>
              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-primary bg-white ring-1 ring-primary/25 px-4 py-1.5 rounded-full mb-6">
                <Network className="w-4 h-4" /> Integration Solutions
              </span>
              <h1 className="text-4xl sm:text-5xl xl:text-[3.7rem] font-extrabold leading-[1.08] tracking-tight mb-6" style={{ color: '#13294b' }}>
                Powerful & Secure API Development
              </h1>
              <p className="text-lg text-slate-500 max-w-xl leading-relaxed mb-8">
                Connect your digital ecosystem. We build robust, well-documented APIs to seamlessly integrate your internal systems and external services.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-semibold text-white shadow-lg shadow-primary/30 bg-primary hover:brightness-110 transition">
                  Start Your Project <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* RIGHT SIDE: Image replacing mockups */}
            <div className="relative se-fade-up w-full" style={{ animationDelay: '.15s' }}>
              <div className="absolute inset-x-8 top-4 bottom-16 bg-gradient-to-tr from-[#3284c7]/15 to-[#60a5fa]/15 blur-3xl rounded-[48px]" />
              <img 
                src={ApiDevelopmentImg} 
                alt="API Development Dashboard Illustration" 
                className="relative z-10 w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CORE MODULES ================= */}
      <section className="bg-gradient-to-b from-[#f5f9fd] to-white py-14">
        <div className={CONTAINER}>
          <div className="text-center mb-10">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Our Offerings</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>API Development Expertise</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature, index) => (
              <div key={index} className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${THEME_COLOR}1A` }}>
                  <feature.icon className="w-6 h-6" style={{ color: THEME_COLOR }} />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-3">{feature.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApiDevelopment;